/**
 * HTTP Client utility for API calls
 * Following single responsibility principle
 */

import { isAbortError } from "./errors/isAbortError";

interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

interface RequestConfig {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  signal?: AbortSignal;
}

/**
 * The timeout for a request that moves a file — an upload, a download or a
 * report carrying its file — rather than the client's ten-second default.
 * The timer covers the whole transfer, body included, so it has to allow for
 * a large file on a slow store connection, not just for the server to start.
 */
export const FILE_TRANSFER_TIMEOUT_MS = 5 * 60 * 1000;

const getHttpErrorMessage = (status: number): string => {
  if (status === 401) {
    return "A sessão expirou. Inicie sessão novamente e tente outra vez.";
  }

  if (status === 403) {
    return "Não tem permissões para concluir esta ação.";
  }

  if (status === 408) {
    return "O servidor demorou demasiado a responder. Tente novamente.";
  }

  if (status === 404) {
    return "Não foi possível encontrar o recurso pedido. Atualize a página e tente novamente.";
  }

  if (status >= 500) {
    return "Não foi possível comunicar com o servidor. Atualize a página e tente novamente.";
  }

  return "Não foi possível concluir o pedido. Confirme os dados e tente novamente.";
};

export class HttpError extends Error {
  status: number;
  statusText?: string;
  responseText?: string;
  responseData?: any;
  url?: string;
  method?: RequestConfig["method"];

  constructor(args: {
    status: number;
    message: string;
    statusText?: string;
    responseText?: string;
    responseData?: any;
    url?: string;
    method?: RequestConfig["method"];
  }) {
    super(args.message);
    this.name = "HttpError";
    this.status = args.status;
    this.statusText = args.statusText;
    this.responseText = args.responseText;
    this.responseData = args.responseData;
    this.url = args.url;
    this.method = args.method;
  }
}

export class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;

  constructor(config: HttpClientConfig) {
    this.baseURL = config.baseURL?.replace(/\/$/, ""); // Remove trailing slash
    this.timeout = config.timeout || 10000; // 10 seconds default
    this.defaultHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("authKey")}`,
      ...config.headers,
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    config: RequestConfig = {},
    idFromLocation = false,
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const {
      method = "GET",
      headers = {},
      body,
      timeout = this.timeout,
      signal: externalSignal,
    } = config;

    const controller = new AbortController();
    /*
     * A timeout aborts the request too, but is not the caller cancelling: it is
     * rejected as an HttpError, so callers that ignore aborts still report it.
     */
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    /* Removed once the request settles, so a reused signal gathers nothing. */
    const onExternalAbort = () => controller.abort();
    if (externalSignal?.aborted) controller.abort();
    externalSignal?.addEventListener("abort", onExternalAbort, { once: true });

    try {
      // Prepare headers - don't set Content-Type for FormData
      const requestHeaders = { ...this.defaultHeaders, ...headers };
      if (body instanceof FormData) {
        delete requestHeaders["Content-Type"];
      }

      const requestInit: RequestInit = {
        method,
        headers: {
          ...requestHeaders,
          Authorization: `Bearer ${localStorage.getItem("authKey")}`,
        },
        signal: controller.signal,
      };

      if (body && method !== "GET") {
        if (body instanceof FormData) {
          requestInit.body = body;
        } else {
          requestInit.body =
            typeof body === "string" ? body : JSON.stringify(body);
        }
      }

      /* The timer stays on until the body is read, so a stalled body times out. */
      const response = await fetch(url, requestInit);

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorData = "";
        let responseData: any;

        /*
         * An unreadable body (cut off by the timeout, or malformed) must not
         * hide the status: the HttpError below still carries it.
         */
        try {
          if (contentType && contentType.includes("application/json")) {
            responseData = await response.json();
            errorData = responseData.message || JSON.stringify(responseData);
          } else {
            errorData = await response.text();
          }
        } catch (bodyError) {
          /* The caller's own cancel stays a cancel. */
          if (externalSignal?.aborted) throw bodyError;
          responseData = undefined;
        }

        throw new HttpError({
          status: response.status,
          statusText: response.statusText,
          responseText: errorData,
          responseData,
          url,
          method,
          message: getHttpErrorMessage(response.status),
        });
      }

      if (idFromLocation) {
        const locationHeader = response.headers.get("Location");
        if (locationHeader) {
          const idSegment = locationHeader.split("/").pop()?.split("?")[0];
          const id = idSegment ? Number(idSegment) : NaN;
          return id as T;
        }
      }

      // Handle different response types
      const contentType = response.headers.get("content-type");

      // Handle blob responses (for file downloads)
      if (
        contentType &&
        (contentType.includes("application/octet-stream") ||
          contentType.includes("application/pdf"))
      ) {
        return (await response.blob()) as T;
      }

      // Handle empty responses or non-JSON content
      if (!contentType || !contentType.includes("application/json")) {
        return {} as T;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* Only the abort the timer caused; a real HttpError keeps its status. */
      if (timedOut && isAbortError(error) && !externalSignal?.aborted) {
        throw new HttpError({
          status: 408,
          statusText: "Request Timeout",
          url,
          method,
          message: getHttpErrorMessage(408),
        });
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", onExternalAbort);
    }
  }

  async get<T>(
    endpoint: string,
    config: Omit<RequestConfig, "method" | "body"> = {},
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: "GET" });
  }

  async post<T>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
    idFromLocation = false,
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        ...config,
        method: "POST",
        body,
      },
      idFromLocation,
    );
  }

  async put<T>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
    idFromLocation = false,
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      { ...config, method: "PUT", body },
      idFromLocation,
    );
  }

  async patch<T>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
    idFromLocation = false,
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      { ...config, method: "PATCH", body },
      idFromLocation,
    );
  }

  async delete<T>(
    endpoint: string,
    config: Omit<RequestConfig, "method" | "body"> = {},
    idFromLocation = false,
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      { ...config, method: "DELETE" },
      idFromLocation,
    );
  }
}
